import React, { useCallback, useEffect, useState, useRef } from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ProductCategoriesService from "services/productCategories.service";
import Loader from "../../assets/img/loader.gif";
import Card from "components/card";
import EventService from "services/events.service";
import InputField from "components/fields/InputField";
import { IoMdClose } from "react-icons/io";
import { AiFillEdit } from "react-icons/ai";
import { FiPlus } from "react-icons/fi";
import tabCache from "utils/tabCache";

const ProductCategories = () => {
  // Initialize from cache if available
  const [categories, setCategories] = useState(tabCache.productCategories.data || []);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(tabCache.productCategories.firstLoad);
  const [modalOpen, setModalOpen] = useState(false);
  const [subCategoryModalOpen, setSubCategoryModalOpen] = useState(false);
  const [selectedParentCategory, setSelectedParentCategory] = useState(null);
  const [confirmLoader, setConfirmLoader] = useState(false);
  const [form, setForm] = useState({
    category_name: "",
    category_image: "",
    description: "",
    is_custom: false,
  });
  const [subCategoryForm, setSubCategoryForm] = useState({
    category_name: "",
    category_image: "",
    description: "",
    parent_category_id: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [imageUploading, setImageUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [subCategoryImageUploading, setSubCategoryImageUploading] = useState(false);
  const [subCategoryImagePreview, setSubCategoryImagePreview] = useState("");
  const [reorderModalOpen, setReorderModalOpen] = useState(false);
  const [reorderCategories, setReorderCategories] = useState([]);
  // Drag state removed - DnD Kit handles this internally
  const [reorderLoading, setReorderLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedEditCategory, setSelectedEditCategory] = useState(null);
  const [editForm, setEditForm] = useState({
    category_id: "",
    subcategory_id: "",
    category_name: "",
    category_image: "",
    description: "",
    is_active: true,
    is_custom: false,
    parent_category_id: "",
  });
  const [editImageUploading, setEditImageUploading] = useState(false);
  const [editImagePreview, setEditImagePreview] = useState("");
  
  const modalRef = useRef();
  const subCategoryModalRef = useRef();
  const reorderModalRef = useRef();
  const reorderScrollRef = useRef();
  const scrollRAFRef = useRef(null);
  const scrollDirRef = useRef(0); // -1 up, 1 down, 0 none
  const scrollSpeedRef = useRef(0); // Current scroll speed
  const lastClientYRef = useRef(0);
  const editModalRef = useRef();

  const fetchCategories = async () => {
    const isFirstLoad = tabCache.productCategories.firstLoad;
    
    // Show loader only on first load
    if (isFirstLoad) {
      setIsLoading(true);
    }
    
    try {
      const response = await ProductCategoriesService.getAllProductCategories();
      console.log('API Response:', response.data);
      const data = response.data.data;
      
      let categoriesData = [];
      
      // Handle different response structures
      if (data.categories) {
        categoriesData = data.categories;
      } else if (Array.isArray(data)) {
        categoriesData = data;
      } else if (data.data && Array.isArray(data.data)) {
        categoriesData = data.data;
      } else {
        console.log('Unexpected data structure:', data);
        categoriesData = [];
      }
      
      // If we still have pagination info and want to get all data
      if (data.pagination && data.pagination.pages > 1) {
        console.log(`Found ${data.pagination.pages} pages, fetching all data...`);
        const allCategories = await fetchAllCategories(data.pagination.pages, categoriesData);
        categoriesData = allCategories;
      }
      
      // Update state and cache
      setCategories(categoriesData);
      tabCache.productCategories.data = categoriesData;
      
      // After first API call, don't show loader next time
      if (isFirstLoad) {
        tabCache.productCategories.firstLoad = false;
        setFirstLoad(false);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      const emptyData = [];
      setCategories(emptyData);
      tabCache.productCategories.data = emptyData;
      if (isFirstLoad) {
        tabCache.productCategories.firstLoad = false;
        setFirstLoad(false);
      }
    } finally {
      if (isFirstLoad) {
        setIsLoading(false);
      }
    }
  };

  const fetchAllCategories = async (totalPages, initialCategories = []) => {
    try {
      const allCategories = [...initialCategories];
      
      for (let page = 2; page <= totalPages; page++) {
        const response = await ProductCategoriesService.getAllProductCategories({
          page,
          pageSize: 1000,
          is_active: true
        });
        const data = response.data.data;
        
        if (data.categories) {
          allCategories.push(...data.categories);
        } else if (Array.isArray(data)) {
          allCategories.push(...data);
        }
      }
      
      console.log(`Fetched all ${allCategories.length} categories`);
      return allCategories;
    } catch (error) {
      console.error('Error fetching all categories:', error);
      return initialCategories;
    }
  };

  // Sortable wrapper for a category item
  const SortableCategoryItem = ({ id, children }) => {
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({ id });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      zIndex: isDragging ? 50 : 'auto',
    };
    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        {children}
      </div>
    );
  };

  useEffect(() => {
    // If we have cached data, show it immediately and fetch in background
    if (tabCache.productCategories.data) {
      // Data already shown from initial state, just fetch in background
      fetchCategories();
    } else {
      // No cached data, fetch and show loader
      fetchCategories();
    }
    // eslint-disable-next-line
  }, []);

  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories(prev => {
      const isCurrentlyOpen = prev[categoryId];
      if (isCurrentlyOpen) {
        // If it's open, just close it
        return {
          ...prev,
          [categoryId]: false
        };
      } else {
        // If it's closed, open it and close all others
        return {
          [categoryId]: true
        };
      }
    });
  };

  // Modal close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setModalOpen(false);
        setForm({ category_name: "", category_image: "", description: "", is_custom: false });
        setError("");
        setSuccess("");
        setImagePreview("");
      }
      if (subCategoryModalRef.current && !subCategoryModalRef.current.contains(event.target)) {
        setSubCategoryModalOpen(false);
        setSubCategoryForm({ category_name: "", category_image: "", description: "", parent_category_id: "" });
        setSelectedParentCategory(null);
        setError("");
        setSuccess("");
        setSubCategoryImagePreview("");
      }
      if (reorderModalRef.current && !reorderModalRef.current.contains(event.target)) {
        setReorderModalOpen(false);
        setReorderCategories([]);
        setError("");
        setSuccess("");
      }
      if (editModalRef.current && !editModalRef.current.contains(event.target)) {
        setEditModalOpen(false);
        setSelectedEditCategory(null);
        setEditForm({ category_id: "", subcategory_id: "", category_name: "", category_image: "", description: "", is_active: true, is_custom: false, parent_category_id: "" });
        setEditImagePreview("");
        setError("");
        setSuccess("");
      }
    };
    if (modalOpen || subCategoryModalOpen || reorderModalOpen || editModalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [modalOpen, subCategoryModalOpen, reorderModalOpen, editModalOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubCategoryInputChange = (e) => {
    setSubCategoryForm({ ...subCategoryForm, [e.target.name]: e.target.value });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageUploading(true);
    setError("");
    setSuccess("");
    setImagePreview("");
    try {
      const formData = new FormData();
      formData.append("files", file);
      const response = await EventService.fileUpload(formData);
      const url = response?.data?.files?.[0];
      setForm((prev) => ({ ...prev, category_image: url }));
      setImagePreview(url);
    } catch (err) {
      setError("Image upload failed. Please try again.");
    } finally {
      setImageUploading(false);
    }
  };

  const handleSubCategoryImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSubCategoryImageUploading(true);
    setError("");
    setSuccess("");
    setSubCategoryImagePreview("");
    try {
      const formData = new FormData();
      formData.append("files", file);
      const response = await EventService.fileUpload(formData);
      const url = response?.data?.files?.[0];
      setSubCategoryForm((prev) => ({ ...prev, category_image: url }));
      setSubCategoryImagePreview(url);
    } catch (err) {
      setError("Image upload failed. Please try again.");
    } finally {
      setSubCategoryImageUploading(false);
    }
  };

  const handleEditImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setEditImageUploading(true);
    setError("");
    setSuccess("");
    setEditImagePreview("");
    try {
      const formData = new FormData();
      formData.append("files", file);
      const response = await EventService.fileUpload(formData);
      const url = response?.data?.files?.[0];
      setEditForm((prev) => ({ ...prev, category_image: url }));
      setEditImagePreview(url);
    } catch (err) {
      setError("Image upload failed. Please try again.");
    } finally {
      setEditImageUploading(false);
    }
  };

  const handleOpenEdit = (category, isSubcategory = false) => {
    setSelectedEditCategory(category);
    setEditForm({
      category_id: isSubcategory ? "" : category._id,
      subcategory_id: isSubcategory ? category._id : "",
      category_name: category.category_name || "",
      category_image: category.category_image || "",
      description: category.description || "",
      is_active: true,
      is_custom: category.is_custom || false,
      parent_category_id: isSubcategory ? category.parent_category_id || "" : "",
    });
    setEditImagePreview(category.category_image || "");
    setError("");
    setSuccess("");
    setEditModalOpen(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm({ ...editForm, [name]: type === 'checkbox' ? checked : value });
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    setConfirmLoader(true);
    setError("");
    setSuccess("");
    try {
      const isSubcategory = !!editForm.subcategory_id;
      const requiredFields = isSubcategory 
        ? ['subcategory_id', 'category_name']
        : ['category_id', 'category_name', 'category_image'];
      
      const missingFields = requiredFields.filter(field => !editForm[field]);
      if (missingFields.length > 0) {
        setError("All required fields must be filled.");
        setConfirmLoader(false);
        return;
      }

      if (isSubcategory) {
        // For subcategories, use the subcategory update endpoint
        const subcategoryPayload = {
          subcategory_id: editForm.subcategory_id,
          category_name: editForm.category_name,
          is_active: editForm.is_active,
          ...(editForm.description && editForm.description.trim() && { description: editForm.description })
        };
        await ProductCategoriesService.updateSubCategory(subcategoryPayload);
      } else {
        // For parent categories, use the category update endpoint
        const categoryPayload = {
          category_id: editForm.category_id,
          category_name: editForm.category_name,
          category_image: editForm.category_image,
          is_active: editForm.is_active,
          is_custom: editForm.is_custom,
          ...(editForm.description && editForm.description.trim() && { description: editForm.description })
        };
        await ProductCategoriesService.updateCategory(categoryPayload);
      }
      
      setSuccess("Category updated successfully!");
      setEditModalOpen(false);
      setSelectedEditCategory(null);
      setEditForm({ category_id: "", subcategory_id: "", category_name: "", category_image: "", description: "", is_active: true, parent_category_id: "" });
      setEditImagePreview("");
      fetchCategories();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update category.");
    } finally {
      setConfirmLoader(false);
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    setConfirmLoader(true);
    setError("");
    setSuccess("");
    try {
      if (!form.category_name || !form.category_image) {
        setError("All fields are required.");
        setConfirmLoader(false);
        return;
      }
      const payload = {
        category_name: form.category_name,
        category_image: form.category_image,
        is_custom: !!form.is_custom,
        ...(form.description && form.description.trim() && { description: form.description })
      };
      await ProductCategoriesService.createCategory(payload);
      setSuccess("Category created successfully!");
      setModalOpen(false);
      setForm({ category_name: "", category_image: "", description: "" });
      setImagePreview("");
      fetchCategories();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to create category.");
    } finally {
      setConfirmLoader(false);
    }
  };

  const handleAddSubCategory = async (e) => {
    e.preventDefault();
    setConfirmLoader(true);
    setError("");
    setSuccess("");
    try {
      if (!subCategoryForm.category_name) {
        setError("All fields are required.");
        setConfirmLoader(false);
        return;
      }
      const payload = {
        category_name: subCategoryForm.category_name,
        parent_category_id: subCategoryForm.parent_category_id,
        ...(subCategoryForm.category_image && { category_image: subCategoryForm.category_image }),
        ...(subCategoryForm.description && subCategoryForm.description.trim() && { description: subCategoryForm.description })
      };
      await ProductCategoriesService.createSubCategory(payload);
      setSuccess("Sub-category created successfully!");
      setSubCategoryModalOpen(false);
      setSubCategoryForm({ category_name: "", category_image: "", description: "", parent_category_id: "" });
      setSelectedParentCategory(null);
      setSubCategoryImagePreview("");
      fetchCategories();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to create sub-category.");
    } finally {
      setConfirmLoader(false);
    }
  };

  // Reorder functions
  const handleOpenReorderModal = () => {
    setReorderCategories([...categories]);
    setReorderModalOpen(true);
    setError("");
    setSuccess("");
  };

  // dnd-kit sensors for pointer with tiny activation distance
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  // dnd-kit drag end handler to reorder array
  const handleDndDragEnd = (event) => {
    const {active, over} = event;
    if (!over || active.id === over.id) return;
    const oldIndex = reorderCategories.findIndex((c) => c._id === active.id);
    const newIndex = reorderCategories.findIndex((c) => c._id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newOrder = arrayMove(reorderCategories, oldIndex, newIndex);
    setReorderCategories(newOrder);
  };

  // Legacy helpers removed - DnD Kit handles everything

  // Legacy handlers removed - DnD Kit handles everything

  // Remove global listeners; dnd-kit handles auto-scroll internally
  useEffect(() => {
    return () => {
      scrollDirRef.current = 0;
      if (scrollRAFRef.current) {
        window.cancelAnimationFrame(scrollRAFRef.current);
        scrollRAFRef.current = null;
      }
    };
  }, [reorderModalOpen]);

  // handleDrop removed - DnD Kit handles reordering via handleDndDragEnd

  const handleSaveOrder = async () => {
    setReorderLoading(true);
    setError("");
    setSuccess("");
    
    try {
      const categoryIds = reorderCategories.map(cat => cat._id);
      await ProductCategoriesService.updateCategoryOrder(categoryIds, false);
      setSuccess("Category order updated successfully!");
      setReorderModalOpen(false);
      setReorderCategories([]);
      fetchCategories(); // Refresh the main list
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update category order.");
    } finally {
      setReorderLoading(false);
    }
  };

  return (
    <div className="-mt-2 grid h-full grid-cols-1 gap-5">
      <div className="col-span-1 h-full w-full rounded-xl 2xl:col-span-1">
        <Card extra={"w-full h-full px-4 sm:px-6 pb-6 overflow-hidden"}>
          <div className="mt-4 mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
            <div className="flex flex-row gap-3 -mr-4">
              <button
                className="h-11 w-fit max-w-[120px] whitespace-nowrap rounded-full bg-orangePrimary px-3 text-sm text-white duration-150 hover:bg-orangePrimary/80 sm:max-w-none sm:px-4 sm:text-base"
                onClick={handleOpenReorderModal}
              >
                Edit Order
              </button>
              <button
                className="h-11 w-fit max-w-[120px] whitespace-nowrap rounded-full bg-orangePrimary px-3 text-sm text-white duration-150 hover:bg-orangePrimary/80 sm:max-w-none sm:px-4 sm:text-base"
                onClick={() => {
                  setError("");
                  setSuccess("");
                  setModalOpen(true);
                }}
              >
                + Add Category
              </button>
            </div>
          </div>
          <style>{`
            @keyframes fadeInSlide {
              from {
                opacity: 0;
                transform: translateX(-10px);
              }
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
          `}</style>
          <div
              className={`table-scroll mt-4 relative ${
                isLoading
                  ? "h-[75vh] overflow-hidden"
                  : "overflow-x-auto overflow-y-hidden"
              }`}
            >
              {isLoading && (
                <div className="pointer-events-none absolute inset-0 z-40 hidden items-center justify-center md:flex">
                  <img src={Loader} alt="Loading..." className="h-16 w-16" />
                </div>
              )}
            <table
                className={`w-full ${isLoading ? "table-fixed" : ""}`}
                style={
                  isLoading
                    ? { tableLayout: "fixed", width: "100%" }
                    : {
                        tableLayout: "auto",
                        minWidth: "max-content",
                        width: "100%",
                      }
                }
              >
                <thead className={isLoading ? "hidden" : ""}>
                  <tr className="!border-px !border-gray-400">
                    <th className="w-[70px] min-w-[50px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Number
                    </th>
                    <th className="w-[100px] min-w-[80px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Image
                    </th>
                    <th className="w-[200px] min-w-[120px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Category Name
                    </th>
                    <th className="w-[200px] min-w-[150px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Description
                    </th>
                    <th className="w-[150px] min-w-[100px] text-sm font-bold uppercase text-white text-left align-middle pl-1 pr-2 sm:pl-1 sm:pr-3">
                      Sub-Categories
                    </th>
                    <th className="w-[150px] min-w-[120px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-4">
                      Actions
                    </th>
                  </tr>
                </thead>
            <tbody>
                  {isLoading ? (
                    <tr className="md:hidden">
                      <td colSpan={6} className="py-6">
                        <div className="flex justify-center">
                          <img
                            src={Loader}
                            alt="Loading..."
                            className="h-16 w-16"
                          />
                        </div>
                      </td>
                    </tr>
                  ) : categories.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-4 text-center">
                        <p className="my-3">No categories found</p>
                      </td>
                    </tr>
                  ) : (
                    categories.map((cat, idx) => (
                      <React.Fragment key={cat._id}>
                        <tr
                          className={`${
                            expandedCategories[cat._id]
                              ? ""
                              : "border-b border-[#fbfbfb1a]"
                          } last:border-none`}
                        >
                          <td className="py-3 px-2 sm:px-3 text-left align-middle">
                            <span className="text-white text-sm">
                              {idx + 1}
                            </span>
                          </td>
                          <td className="py-3 px-2 sm:px-3 text-left align-middle">
                            <img
                              src={cat.category_image}
                              alt={cat.category_name}
                              className="h-12 w-12 rounded-lg object-cover"
                            />
                          </td>
                          <td className="py-3 px-2 sm:px-3 font-bold text-white text-left align-middle">
                            <div className="flex items-center gap-2">
                              <span className="truncate max-w-[150px] sm:max-w-none">
                                {cat.category_name}
                              </span>
                              {cat.is_custom && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-purple-600/20 text-purple-300 border-purple-500/30 flex-shrink-0">
                                  Custom
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-2 sm:px-3 text-white text-left align-middle">
                            <div
                              className={`max-w-[200px] sm:max-w-[250px] truncate ${
                                !cat.description ? "ml-5" : ""
                              }`}
                              title={cat.description || ""}
                            >
                              {cat.description || "--"}
                            </div>
                          </td>
                          <td className="py-3 pl-1 pr-2 sm:pl-1 sm:pr-3 text-left align-middle">
                            {cat.subcategories && cat.subcategories.length > 0 ? (
                              <button
                                onClick={() =>
                                  toggleCategoryExpansion(cat._id)
                                }
                                className="px-2 sm:px-3.5 py-1.5 sm:py-2 bg-[#369FFF1A]/10 text-[#369FFF] rounded-lg transition-all text-xs sm:text-sm font-semibold min-w-[50px] sm:min-w-[70px] duration-150 cursor-pointer flex items-center justify-between whitespace-nowrap hover:bg-[#369FFF1A]/20"
                                title={
                                  expandedCategories[cat._id]
                                    ? "Hide Sub-categories"
                                    : "Show Sub-categories"
                                }
                              >
                                <span className="text-xs sm:text-sm font-bold">
                                  {cat.subcategories.length}
                                </span>
                                <span className="text-xs">
                                  {expandedCategories[cat._id] ? "▲" : "▼"}
                                </span>
                              </button>
                            ) : (
                              <span className="ml-2 sm:ml-6 text-gray-500 text-sm">
                                --
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-2 sm:px-3 text-left align-middle">
                            <div className="flex items-center gap-3">
                              <div
                                className="ms-center flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg bg-[#369FFF1A]/10 text-[#369FFF] duration-150"
                                onClick={() => {
                                  setError("");
                                  setSuccess("");
                                  setSelectedParentCategory(cat);
                                  setSubCategoryForm({
                                    ...subCategoryForm,
                                    parent_category_id: cat._id,
                                  });
                                  setSubCategoryModalOpen(true);
                                }}
                                title="Add Sub-Category"
                              >
                                <FiPlus />
                              </div>
                              <div
                                className="ms-center flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg bg-[#FF80001A]/10 text-[#FF8000] duration-150"
                                onClick={() => handleOpenEdit(cat, false)}
                                title="Edit Category"
                              >
                                <AiFillEdit />
                              </div>
                            </div>
                          </td>
                        </tr>
                        {/* Sub-Categories Row */}
                        {cat.subcategories &&
                          cat.subcategories.length > 0 &&
                          expandedCategories[cat._id] && (
                            <tr>
                              <td colSpan={6} className="py-4 px-3 sm:px-6">
                                <div className="animate-[fadeIn_0.3s_ease-in-out_forwards]">
                                  <div className="rounded-xl bg-navy-800 border border-white/10 p-4">
                                    <div className="flex items-center gap-1 mb-4">
                                      <span className="text-sm font-semibold text-blue-400 pl-1 uppercase tracking-wide">
                                        Sub-Categories
                                      </span>
                                      <span className="text-sm  text-blue-400 font-bold">
                                        ({cat.subcategories.length})
                                      </span>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                                      {cat.subcategories.map(
                                        (subCat, subIdx) => (
                                          <div
                                            key={subCat._id}
                                            className="bg-navy-800/70 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 border border-white/10 hover:border-blue-500/40 hover:bg-navy-800/90 transition-all duration-300 flex items-center justify-between gap-2 shadow-md hover:shadow-lg hover:scale-[1.02]"
                                            style={{
                                              animation: `fadeInSlide 0.4s ease-out ${
                                                subIdx * 0.05
                                              }s forwards`,
                                              opacity: 0,
                                            }}
                                          >
                                            <div className="flex items-center gap-2 sm:gap-2.5 flex-1 min-w-0 max-w-[calc(100%-80px)] sm:max-w-[calc(100%-90px)]">
                                              <span className="text-white text-xs sm:text-sm font-medium truncate">
                                                {subCat.category_name}
                                              </span>
                                            </div>
                                            <div
                                              className="ms-center flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg bg-[#FF80001A]/10 text-[#FF8000] duration-150"
                                              onClick={() =>
                                                handleOpenEdit(subCat, true)
                                              }
                                              title="Edit Sub-Category"
                                            >
                                              <AiFillEdit />
                                            </div>
                                          </div>
                                        )
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                      </React.Fragment>
                    ))
                  )}
            </tbody>
            </table>
          </div>
        </Card>
      </div>
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div className="mx-3 flex h-[85vh] w-[420px] flex-col rounded-xl bg-navy-800 shadow-lg" ref={modalRef}>
            {/* Fixed Header */}
            <div className="relative flex justify-center border-b border-white/10 py-4 px-6">
              <p className="text-2xl font-semibold text-white">Add Category</p>
              <IoMdClose
                className="absolute right-6 cursor-pointer text-2xl text-white hover:text-gray-300"
                onClick={() => {
                  setModalOpen(false);
                  setForm({ category_name: "", category_image: "", description: "", is_custom: false });
                  setError("");
                  setSuccess("");
                  setImagePreview("");
                }}
              />
            </div>
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4" ref={reorderScrollRef}>
              <form onSubmit={handleAddCategory} autoComplete="off">
                <InputField
                  label="Category Name*"
                  id="category_name"
                  name="category_name"
                  type="text"
                  placeholder="Category Name"
                  variant="form"
                  extra="mb-4"
                  value={form.category_name}
                  onChange={handleInputChange}
                />
                <div className="mb-4">
                  <label className="block text-gray-300 mb-1">Category Image*</label>
                  <input
                    type="file"
                    accept="image/*"
                    id="category_image_upload"
                    style={{ display: 'none' }}
                    onChange={handleImageChange}
                  />
                  {imageUploading ? (
                    <div className="flex min-h-11 w-full items-center justify-center rounded-xl border border-dashed border-white/10 mt-2 bg-transparent">
                      <img src={Loader} alt="Loading..." className="mx-auto h-10 w-10" />
                    </div>
                  ) : imagePreview ? (
                    <label htmlFor="category_image_upload" className="mb-5 mt-1 h-full w-full cursor-pointer">
                      <img src={imagePreview} alt="Preview" className="h-full w-full rounded-xl object-cover bg-transparent" style={{ height: 220 }} />
                    </label>
                  ) : (
                    <label
                      htmlFor="category_image_upload"
                      className="flex h-full min-h-11 w-full cursor-pointer items-center justify-center rounded-xl border border-dashed border-white/10 text-white duration-300 hover:bg-white/10 bg-transparent"
                    >
                      Choose File
                    </label>
                  )}
                </div>
                <InputField
                  label="Description*"
                  id="description"
                  name="description"
                  type="text"
                  placeholder="Description"
                  variant="form"
                  extra="mb-4"
                  value={form.description}
                  onChange={handleInputChange}
                />
                <div className="mb-4 flex items-center gap-3">
                  <input
                    id="is_custom"
                    name="is_custom"
                    type="checkbox"
                    checked={!!form.is_custom}
                    onChange={handleInputChange}
                    className="h-4 w-4"
                  />
                  <label htmlFor="is_custom" className="text-gray-300">Is Custom</label>
                </div>
                {error && <p className="mb-2 text-red-500 text-sm">{error}</p>}
                {success && <p className="mb-2 text-green-500 text-sm">{success}</p>}
              </form>
            </div>
            {/* Fixed Footer */}
            <div className="border-t border-white/10 px-6 py-4">
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setModalOpen(false);
                    setForm({ category_name: "", category_image: "", description: "", is_custom: false });
                    setError("");
                    setSuccess("");
                    setImagePreview("");
                  }}
                  className="px-5 py-2.5 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all duration-200 font-semibold text-sm border border-white/20"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  onClick={handleAddCategory}
                  className="w-36 rounded-md bg-orangePrimary px-4 py-2 text-white hover:opacity-90"
                  disabled={confirmLoader || imageUploading}
                >
                  {confirmLoader ? (
                    <img src={Loader} alt="Loading..." className="mx-auto h-6 w-6" />
                  ) : (
                    "Add Categories"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {subCategoryModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="mx-3 flex max-h-[90vh] w-full max-w-[500px] flex-col rounded-2xl bg-navy-800 shadow-2xl border border-white/10" ref={subCategoryModalRef}>
            {/* Fixed Header */}
            <div className="relative flex items-center justify-between border-b border-white/10 py-5 px-6">
              <div className="flex items-center gap-3">
                <div className="h-1 w-8 rounded-full bg-gradient-to-r from-blue-400 to-blue-600"></div>
                <p className="text-xl font-bold text-white">Add Sub-Category</p>
              </div>
              <IoMdClose
                className="cursor-pointer text-2xl text-gray-400 hover:text-white transition-colors duration-200"
                onClick={() => {
                  setSubCategoryModalOpen(false);
                  setSubCategoryForm({ category_name: "", category_image: "", description: "", parent_category_id: "" });
                  setSelectedParentCategory(null);
                  setError("");
                  setSuccess("");
                  setSubCategoryImagePreview("");
                }}
              />
            </div>
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-6 py-6">
              {selectedParentCategory && (
                <div className="mb-6 p-4 bg-gradient-to-r from-blue-500/10 to-blue-500/5 rounded-xl border border-blue-500/30 shadow-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-blue-400"></div>
                    <p className="text-xs font-semibold text-blue-300 uppercase tracking-wide">Parent Category</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {selectedParentCategory.category_image && (
                      <img 
                        src={selectedParentCategory.category_image} 
                        alt={selectedParentCategory.category_name}
                        className="h-10 w-10 rounded-lg object-cover border border-blue-500/30"
                      />
                    )}
                    <p className="text-base font-bold text-white">{selectedParentCategory.category_name}</p>
                  </div>
                </div>
              )}
              <form onSubmit={handleAddSubCategory} autoComplete="off">
                <div className="space-y-5">
                  <InputField
                    label="Sub-Category Name*"
                    id="sub_category_name"
                    name="category_name"
                    type="text"
                    placeholder="Enter sub-category name"
                    variant="form"
                    extra="mb-0"
                    value={subCategoryForm.category_name}
                    onChange={handleSubCategoryInputChange}
                  />
                  
                  <InputField
                    label="Description*"
                    id="sub_description"
                    name="description"
                    type="text"
                    placeholder="Enter description"
                    variant="form"
                    extra="mb-0"
                    value={subCategoryForm.description}
                    onChange={handleSubCategoryInputChange}
                  />
                  
                  {error && (
                    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-red-400 text-sm font-medium">{error}</p>
                    </div>
                  )}
                  {success && (
                    <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <p className="text-green-400 text-sm font-medium">{success}</p>
                    </div>
                  )}
                </div>
              </form>
            </div>
            {/* Fixed Footer */}
            <div className="border-t border-white/10 px-6 py-5 bg-navy-800/50">
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setSubCategoryModalOpen(false);
                    setSubCategoryForm({ category_name: "", category_image: "", description: "", parent_category_id: "" });
                    setSelectedParentCategory(null);
                    setError("");
                    setSuccess("");
                    setSubCategoryImagePreview("");
                  }}
                  className="px-5 py-2.5 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all duration-200 font-semibold text-sm border border-white/20"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  onClick={handleAddSubCategory}
                  className="px-6 py-2.5 rounded-lg bg-[#FF80001A]/10 text-[#FF8000] hover:bg-[#FF80001A]/20 transition-all duration-200 font-semibold text-sm border border-[#FF8000]/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[140px]"
                  disabled={confirmLoader}
                >
                  {confirmLoader ? (
                    <img src={Loader} alt="Loading..." className="h-5 w-5" />
                  ) : (
                    "Add"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {reorderModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div className="mx-3 flex h-[80vh] w-[600px] flex-col rounded-xl bg-navy-800 shadow-lg" ref={reorderModalRef}>
            {/* Fixed Header */}
            <div className="relative flex justify-center border-b border-white/10 py-4 px-6">
              <p className="text-2xl font-semibold text-white">Reorder Categories</p>
              <IoMdClose
                className="absolute right-6 cursor-pointer text-2xl text-white hover:text-gray-300"
                onClick={() => {
                  setReorderModalOpen(false);
                  setReorderCategories([]);
                  setError("");
                  setSuccess("");
                }}
              />
            </div>
            {/* Scrollable Content */}
            <div 
              ref={reorderScrollRef}
              className="flex-1 overflow-y-auto px-6 py-4 scroll-smooth"
            >
              <div className="mb-4 p-3 bg-blue-800/10 rounded-lg border border-white/10">
                <p className="text-sm text-white">
                  💡 Drag and drop categories to reorder them. The order will be saved when you click "Save Order".
                </p>
              </div>
              
              {/* DnD Context */}
              <div className="relative">
                
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDndDragEnd}>
                <SortableContext items={reorderCategories.map(c => c._id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-3 relative z-0">
                  {reorderCategories.map((category, index) => (
                    <SortableCategoryItem key={category._id} id={category._id} index={index}>
                    <div>
                      <div
                        className="rounded-lg p-4 border transition-all duration-200 cursor-move group bg-blue-800/10 border-white/10 hover:border-white/30 hover:shadow-md"
                   
                      >
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-7 h-7 bg-blue-400/20 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {index + 1}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <img 
                          src={category.category_image} 
                          alt={category.category_name} 
                          className="h-12 w-12 object-cover rounded-lg border border-white/10" 
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-white font-semibold text-lg">{category.category_name}</h3>
                          {category.is_custom && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-purple-600/20 text-purple-300 border-purple-500/30">
                              Custom
                            </span>
                          )}
                        </div>
                        <p className="text-gray-300 text-sm line-clamp-2">{category.description}</p>
                        {category.subcategories && category.subcategories.length > 0 && (
                          <div className="mt-0.5">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#15290E]/55 text-[#38B600]">
                              {category.subcategories.length} sub-categories
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        <div className="text-gray-600 text-sm group-hover:text-blue-400/50 transition-colors cursor-grab active:cursor-grabbing">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                    </div>
                    </div>
                    </SortableCategoryItem>
                  ))}
                </div>
                </SortableContext>
                </DndContext>
              </div>
              {error && <p className="mt-4 text-red-500 text-sm">{error}</p>}
              {success && <p className="mt-4 text-green-500 text-sm">{success}</p>}
            </div>
            {/* Fixed Footer */}
            <div className="border-t border-white/10 px-6 py-4">
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setReorderModalOpen(false);
                    setReorderCategories([]);
                    setError("");
                    setSuccess("");
                  }}
                  className="px-5 py-2.5 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all duration-200 font-semibold text-sm border border-white/20"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveOrder}
                  className="w-36 rounded-md bg-orangePrimary px-4 py-2 text-white hover:opacity-90"
                  disabled={reorderLoading}
                >
                  {reorderLoading ? (
                    <img src={Loader} alt="Loading..." className="mx-auto h-6 w-6" />
                  ) : (
                    "Save Order"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {editModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="mx-3 flex max-h-[90vh] w-full max-w-[500px] flex-col rounded-2xl bg-navy-800 shadow-2xl border border-white/10" ref={editModalRef}>
            {/* Fixed Header */}
            <div className="relative flex items-center justify-between border-b border-white/10 py-5 px-6">
              <div className="flex items-center gap-3">
                <div className="h-1 w-8 rounded-full bg-gradient-to-r from-blue-400 to-blue-600"></div>
                <p className="text-xl font-bold text-white">Edit Category</p>
              </div>
              <IoMdClose
                className="cursor-pointer text-2xl text-gray-400 hover:text-white transition-colors duration-200"
                onClick={() => {
                  setEditModalOpen(false);
                  setSelectedEditCategory(null);
                  setEditForm({ category_id: "", subcategory_id: "", category_name: "", category_image: "", description: "", is_active: true, is_custom: false, parent_category_id: "" });
                  setEditImagePreview("");
                  setError("");
                  setSuccess("");
                }}
              />
            </div>
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <form onSubmit={handleUpdateCategory} autoComplete="off">
                <div className="space-y-5">
                  <InputField
                    label="Category Name*"
                    id="edit_category_name"
                    name="category_name"
                    type="text"
                    placeholder="Enter category name"
                    variant="form"
                    extra="mb-0"
                    value={editForm.category_name}
                    onChange={handleEditInputChange}
                  />
                  {!editForm.subcategory_id && (
                    <div className="mb-0">
                      <label className="block text-gray-300 mb-2 text-sm font-medium">Category Image*</label>
                      <input
                        type="file"
                        accept="image/*"
                        id="edit_category_image_upload"
                        style={{ display: 'none' }}
                        onChange={handleEditImageChange}
                      />
                      {editImageUploading ? (
                        <div className="flex min-h-11 w-full items-center justify-center rounded-xl border border-dashed border-white/10 mt-2 bg-transparent">
                          <img src={Loader} alt="Loading..." className="mx-auto h-10 w-10" />
                        </div>
                      ) : editImagePreview ? (
                        <label htmlFor="edit_category_image_upload" className="mb-5 mt-1 h-full w-full cursor-pointer">
                          <img src={editImagePreview} alt="Preview" className="h-full w-full rounded-xl object-cover bg-transparent" style={{ height: 220 }} />
                        </label>
                      ) : (
                        <label
                          htmlFor="edit_category_image_upload"
                          className="flex h-full min-h-11 w-full cursor-pointer items-center justify-center rounded-xl border border-dashed border-white/10 text-white duration-300 hover:bg-white/10 bg-transparent"
                        >
                          Choose File
                        </label>
                      )}
                    </div>
                  )}
                  {editForm.subcategory_id && null}
                  <InputField
                    label="Description*"
                    id="edit_description"
                    name="description"
                    type="text"
                    placeholder="Enter description"
                    variant="form"
                    extra="mb-0"
                    value={editForm.description}
                    onChange={handleEditInputChange}
                  />
                  {!editForm.subcategory_id && (
                    <div className="mb-0 flex items-center gap-3">
                      <input
                        id="edit_is_custom"
                        name="is_custom"
                        type="checkbox"
                        checked={!!editForm.is_custom}
                        onChange={handleEditInputChange}
                        className="h-4 w-4"
                      />
                      <label htmlFor="edit_is_custom" className="text-gray-300">Is Custom</label>
                    </div>
                  )}
                  {error && (
                    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-red-400 text-sm font-medium">{error}</p>
                    </div>
                  )}
                  {success && (
                    <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <p className="text-green-400 text-sm font-medium">{success}</p>
                    </div>
                  )}
                </div>
              </form>
            </div>
            {/* Fixed Footer */}
            <div className="border-t border-white/10 px-6 py-5 bg-navy-800/50">
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setEditModalOpen(false);
                    setSelectedEditCategory(null);
                    setEditForm({ category_id: "", subcategory_id: "", category_name: "", category_image: "", description: "", is_active: true, is_custom: false, parent_category_id: "" });
                    setEditImagePreview("");
                    setError("");
                    setSuccess("");
                  }}
                  className="px-5 py-2.5 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all duration-200 font-semibold text-sm border border-white/20"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  onClick={handleUpdateCategory}
                  className="px-6 py-2.5 rounded-lg bg-[#369FFF1A]/10 text-[#369FFF] hover:bg-[#369FFF1A]/20 transition-all duration-200 font-semibold text-sm border border-[#369FFF]/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[140px]"
                  disabled={confirmLoader || editImageUploading}
                >
                  {confirmLoader ? (
                    <img src={Loader} alt="Loading..." className="h-5 w-5" />
                  ) : (
                    "Update"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default ProductCategories; 